import React from "react";
import { BaseTemplate } from "@/components/shared/UniversalEntityPage/types";
import type { TemplateType } from "../utils/getTemplates";

export interface TemplatePreviewProps {
  template: BaseTemplate;
  templateType: TemplateType;
}

export function TemplatePreview({ template, templateType }: TemplatePreviewProps) {
  switch (templateType) {
    case "time-entry":
      return (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-2 h-2 bg-blue-500 rounded-full mt-2"></div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-card-foreground">{template.name}</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{template.description || ""}</p>
            </div>
          </div>
          <div className="border-t pt-3">
            <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Preview Description:</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 bg-card-50 p-2 rounded border">
              {template.description_text}
            </div>
          </div>
          {template.tags && template.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {template.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      );

    case "ticket":
      return (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-2 h-2 bg-green-500 rounded-full mt-2"></div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-card-foreground">{template.name}</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{template.description || ""}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                {template.category && (
                  <span className="font-medium">Category: {template.category}</span>
                )}
                {template.estimated_hours && (
                  <span className="font-medium">Est. Hours: {template.estimated_hours}</span>
                )}
              </div>
            </div>
          </div>
          <div className="border-t pt-3">
            <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Preview Title:</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 bg-card-50 p-2 rounded border mb-2">
              {template.title}
            </div>
            <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Preview Description:</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 bg-card-50 p-2 rounded border">
              {template.description_text}
            </div>
          </div>
          {template.tags && template.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {template.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      );

    case "task":
      return (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-2 h-2 bg-purple-500 rounded-full mt-2"></div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-card-foreground">{template.name}</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{template.description || ""}</p>
              <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                {template.priority && (
                  <span className="font-medium">Priority: {template.priority}</span>
                )}
                {template.status && <span className="font-medium">Status: {template.status}</span>}
              </div>
            </div>
          </div>
          <div className="border-t pt-3">
            <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Preview Title:</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 bg-card-50 p-2 rounded border mb-2">
              {template.title}
            </div>
            <div className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Preview Description:</div>
            <div className="text-xs text-gray-600 dark:text-gray-400 bg-card-50 p-2 rounded border">
              {template.description_text}
            </div>
          </div>
          {template.tags && template.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {template.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      );

    default:
      return (
        <div className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-2 h-2 bg-card-500 rounded-full mt-2"></div>
            <div className="flex-1">
              <h4 className="text-sm font-medium text-card-foreground">{template.name}</h4>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{template.description}</p>
            </div>
          </div>
          {template.tags && template.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {template.tags.map((tag: string) => (
                <span
                  key={tag}
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-card-100 text-gray-800 dark:text-gray-200"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      );
  }
}
